import React, { useState, useMemo, useCallback } from "react"
import { useDroppable } from "@dnd-kit/core"
import ProdPlannerTile from "./ProdPlannerTile"
import SearchBarWithAutocomplete from "../SearchBarWithAutocomplete"
import EmptySearchState from "../EmptySearchState"
import { COLORS } from "../../utils"
import { UNASSIGNED_PANEL_WIDTH } from "../../utils/constant"
import { filterUnassignedTiles, unassignedTilesSearchOption, getUnassignedAutocompleteOptions, isNoMachineCooling } from "../../utils/prodPlanner"

const UnassignedTilesPanel = ({ tiles, setTiles, expandedTiles, toggleTile, isLoading, isAnyTileDragging, recentlyDroppedTileId, noMachineTypeIds }) => {
  const [searchQuery, setSearchQuery] = useState("")
  const [inputValue, setInputValue] = useState("")

  // Register "unassigned" area as a valid drop target in case the user wants to drop back a step
  const { setNodeRef, isOver } = useDroppable({ id: "unassigned" })

  // Get all unassigned tiles first
  const unassignedTiles = useMemo(() => {
    return tiles.filter((tile) => {
      if (tile.machine != null && tile.start != null && tile.end != null) return false
      // Cooling steps realized without a machine are never "to be placed" — exclude from the pending panel
      if (isNoMachineCooling(tile, noMachineTypeIds)) return false
      return true
    })
  }, [tiles, noMachineTypeIds])

  // Generate autocomplete options from unassigned tiles
  const autocompleteOptions = useMemo(() => {
    return getUnassignedAutocompleteOptions(unassignedTiles)
  }, [unassignedTiles])

  // Custom filter function for autocomplete
  const filterOptions = useCallback(unassignedTilesSearchOption, [])

  // Handle search query change
  const handleSearch = useCallback((query) => {
    setInputValue(query)
    setSearchQuery(query)
  }, [])

  // Handle option selection from autocomplete
  const handleSelect = useCallback((option) => {
    const tile = option.value
    setSearchQuery(tile.objectId)
    setInputValue(option.label)
  }, [])

  // Filter tiles based on search query
  const filteredTiles = useMemo(() => {
    return filterUnassignedTiles(unassignedTiles, searchQuery)
  }, [unassignedTiles, searchQuery])

  return (
    <div
      ref={setNodeRef}
      style={{
        ...styles.sidebar,
        backgroundColor: isOver ? COLORS.PRIMARY_BLUE_20 : COLORS.WHITE,
        transition: "background-color 0.2s ease",
      }}
    >
      {/* Search Bar with Autocomplete */}
      <SearchBarWithAutocomplete
        placeholder="Rechercher étape, section, recette"
        options={autocompleteOptions}
        onSearch={handleSearch}
        onSelect={handleSelect}
        customFilter={filterOptions}
        sx={{ padding: "24px 16px 16px 16px" }}
      />

      <div style={{...styles.tilesContainer}}>
        {isLoading && <div> Chargement...</div>}

        {!isLoading && filteredTiles.map((tile, index) => {
          const isNotFirstTile = index > 0 && recentlyDroppedTileId != null
          
          return (
            <div 
              key={tile.objectId || index}
              style={{
                // Animate tiles moving down when a new tile is inserted at the top
                animation: isNotFirstTile ? "slideDown 0.3s ease-out" : "none",
                transition: "transform 0.3s ease-out",
              }}
            >
              <style>
                {`
                  @keyframes slideDown {
                    0% {
                      transform: translateY(-100%);
                      opacity: 0.5;
                    }
                    100% {
                      transform: translateY(0);
                      opacity: 1;
                    }
                  }
                `}
              </style>
              <ProdPlannerTile
                tile={tile}
                setTiles={setTiles}
                isExpanded={expandedTiles[tile.objectId]}
                onToggle={() => toggleTile(tile.objectId)}
                tiles={tiles}
                isAnyTileDragging={isAnyTileDragging}
              />
            </div>
          )
        })}
      
        {!isLoading && filteredTiles.length === 0 && unassignedTiles.length === 0 && (
          <EmptySearchState
            title="Il n'y a aucun résultat pour ce jour de production."
            subtitle="Vérifiez la date sélectionnée."
          />
        )}

        {!isLoading && filteredTiles.length === 0 && unassignedTiles.length > 0 && (
          <EmptySearchState
            title={`Il n'y a aucun résultat pour votre recherche "${inputValue}"`}
            subtitle="Vérifiez l'orthographe ou lancez une autre recherche."
          />
        )}
      </div>
    </div>
  )
}

export default UnassignedTilesPanel

const styles = {
  sidebar: {
    borderLeft: `1px solid ${COLORS.BLACK_200}`,
    overflowY: "auto",
    minWidth: `${UNASSIGNED_PANEL_WIDTH}px`,
    maxWidth: `${UNASSIGNED_PANEL_WIDTH}px`,
    width: `${UNASSIGNED_PANEL_WIDTH}px`,
    position: "relative",
    zIndex: 10, 
    boxSizing: "border-box",
  },
  tilesContainer: {
    padding: "16px 16px 24px 16px",
  },
}
